/**
 * AdminHub — Super Admin landing page.
 *
 * Reorganized as a nested accordion (Category → Tools) so the hub scales
 * as more employer-management features are added without turning into a
 * wall of cards. Every admin capability lives on its own page; this hub
 * is the one place a super-admin picks the tool they want.
 *
 * Structure: 5 top-level categories, each collapsible, each holding a set
 * of tool cards. The user's most-used category (Employers) is expanded
 * by default; the rest collapse to keep the initial view scan-friendly.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Shield, CreditCard, Sparkles, Building2,
    Headphones, LayoutDashboard, Zap, DollarSign,
    Users, TrendingUp, ChevronDown, ChevronRight, FileText,
    Wallet, Palette,
} from "lucide-react";

const GROUPS = [
    {
        id: "employers",
        title: "Employers & tenants",
        subtitle: "Manage individual employer accounts, features and access.",
        color: "border-emerald-300",
        defaultOpen: true,
        cards: [
            { to: "/admin/employers",      icon: Users,      label: "All employers",
              desc: "List, search, approve KYC, impersonate, deactivate, delete." },
            { to: "/admin/features",       icon: Sparkles,   label: "Feature Management",
              desc: "Toggle features per employer, manage presets, clone/preview." },
        ],
    },
    {
        id: "billing",
        title: "Billing & subscriptions",
        subtitle: "Monthly recurring revenue, invoices, custom plans, retries.",
        color: "border-blue-300",
        defaultOpen: true,
        cards: [
            { to: "/admin/subscriptions",  icon: CreditCard, label: "Subscriptions & MRR",
              desc: "Assign custom plans, view MRR, issue invoices, track lifecycle." },
            { to: "/admin/subscription-plans", icon: Sparkles, label: "Plans & pricing",
              desc: "Define plan prices, quotas, marketing bullets, default feature preset." },
            { to: "/admin/campaign-codes", icon: Sparkles,   label: "Campaign codes",
              desc: "Promo codes for /signup?code=X to grant plans or discounts." },
            { to: "/admin/seller-billing", icon: Building2,  label: "Seller billing entity",
              desc: "Portalwiz's own name, GSTIN & address on every invoice." },
        ],
    },
    {
        id: "revenue",
        title: "Revenue & campaigns",
        subtitle: "Spotlight funnel monetization + growth analytics.",
        color: "border-amber-300",
        defaultOpen: false,
        cards: [
            { to: "/admin/spotlight-funnel", icon: DollarSign, label: "Spotlight funnel",
              desc: "Campaign monetization + featured slot analytics." },
        ],
    },
    {
        id: "content",
        title: "Content & marketing",
        subtitle: "Public site content, landing page copy, testimonials.",
        color: "border-violet-300",
        defaultOpen: false,
        cards: [
            { to: "/admin/landing-content", icon: LayoutDashboard, label: "Landing content",
              desc: "Public homepage copy, testimonials and CTAs." },
        ],
    },
    {
        id: "ops",
        title: "Support & ops",
        subtitle: "Helpdesk tickets and system tools.",
        color: "border-rose-300",
        defaultOpen: false,
        cards: [
            { to: "/admin/helpdesk", icon: Headphones, label: "Helpdesk",
              desc: "Employer support tickets + reply threads." },
        ],
    },
];

export default function AdminHub() {
    const [open, setOpen] = useState(() => {
        const seed = {};
        GROUPS.forEach((g) => { seed[g.id] = g.defaultOpen; });
        return seed;
    });
    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-hub-page">
            <header className="mb-4 pb-4 border-b border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Super admin</div>
                <h1 className="font-display font-black text-3xl text-ikb leading-tight">Admin console</h1>
                <p className="text-xs text-muted-foreground mt-1">Categories group related tools. Click a section header to collapse it.</p>
            </header>
            <div className="space-y-3">
                {GROUPS.map((g) => (
                    <section key={g.id} className={`border-l-4 ${g.color} bg-white border-t border-r border-b border-border`}>
                        <button
                            type="button"
                            onClick={() => setOpen((o) => ({ ...o, [g.id]: !o[g.id] }))}
                            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-neutral-50 text-left"
                            data-testid={`admin-hub-group-${g.id}`}
                        >
                            {open[g.id]
                                ? <ChevronDown className="w-4 h-4 text-ikb" />
                                : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-black text-sm text-foreground">{g.title}</div>
                                <div className="text-[11px] text-muted-foreground">{g.subtitle}</div>
                            </div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                                {g.cards.length} tool{g.cards.length === 1 ? "" : "s"}
                            </div>
                        </button>
                        {open[g.id] && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3 border-t border-border">
                                {g.cards.map((c) => (
                                    <Link
                                        key={c.to + c.label}
                                        to={c.to}
                                        className="block border border-border bg-white hover:border-ikb hover:shadow-sm p-4 transition-all"
                                        data-testid={`admin-hub-card-${c.to.replace(/\//g, "-").slice(1)}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <c.icon className="w-4 h-4 text-ikb" />
                                            <div className="font-bold text-sm">{c.label}</div>
                                        </div>
                                        <div className="text-[11px] text-muted-foreground">{c.desc}</div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </section>
                ))}
            </div>
        </div>
    );
}
